import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useCheckbox } from '../checkbox/useCheckbox';
import { Group } from './Group';
import { RadioTag } from './RadioTag';
import { Box } from '../grid/Grid';

const InputWrapper = styled.span`
  display: inline-block;
`;

const Indicator = styled.span`
  box-sizing: border-box;
  position: relative;
  display: block;
  width: ${themeGet('radio.indicator.size')};
  height: ${themeGet('radio.indicator.size')};
  border: ${themeGet('radio.indicator.border')};
  border-radius: ${themeGet('radio.indicator.borderRadius')};
  border-color: ${themeGet('radio.indicator.borderColor')};
  background-color: ${themeGet('radio.indicator.backgroundColor')};
  transition: background-color 180ms;

  ${props =>
    props.checked &&
    css`
      border-color: ${themeGet('radio.indicator.checked.borderColor')};
      background-color: ${themeGet('radio.indicator.checked.backgroundColor')};
    `}

  ${props =>
    props.disabled &&
    css`
      border-color: ${themeGet('radio.indicator.disabled.borderColor')};
      background-color: ${themeGet('radio.indicator.disabled.backgroundColor')};
    `}
    
  ${props =>
    props.checked &&
    props.disabled &&
    css`
      border-color: ${themeGet('radio.indicator.disabledChecked.borderColor')};
      background-color: ${themeGet('radio.indicator.disabledChecked.backgroundColor')};
    `}
`;

const Input = styled.input`
  display: none;
`;

const Wrapper = styled(Box).attrs(() => ({
  as: 'label',
}))`
  white-space: nowrap;
  cursor: pointer;

  &:hover ${Indicator} {
    border-color: ${themeGet('radio.indicator.hover.borderColor')};
  }

  ${props =>
    props.disabled &&
    css`
      cursor: not-allowed;

      &:hover ${Indicator} {
        border-color: ${props.checked
          ? themeGet('radio.indicator.disabledChecked.borderColor')
          : themeGet('radio.indicator.disabled.borderColor')};
      }
    `}
`;
Wrapper.defaultProps = {
  display: 'inline-flex',
  alignItems: 'center',
  flex: 1,
};

const Text = styled.span`
  padding: 0 8px;
  font-size: ${themeGet('radio.text.fontSize')};
  user-select: none;
  color: ${themeGet('radio.text.color')};

  ${props =>
    props.disabled &&
    css`
      color: ${themeGet('radio.text.disabled.color')};
    `}
`;

const Circle = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 45%;
  height: 45%;
  border-radius: 50%;
  border-top: 0;
  border-left: 0;
  background-color: ${themeGet('radio.indicator.icon.backgroundColor')};
  transform: translate(-50%, -50%);
`;

export const Radio = forwardRef(({ disabled, children, checked, name, value, onChange, ...rest }, ref) => {
  const { isChecked, handleChange } = useCheckbox({ checked, onChange });

  return (
    <Wrapper checked={isChecked} disabled={disabled} {...rest} ref={ref}>
      <InputWrapper>
        <Input
          type="radio"
          name={name}
          value={value}
          checked={isChecked}
          disabled={disabled}
          onChange={handleChange}
        />
        <Indicator checked={isChecked} disabled={disabled}>
          {isChecked && <Circle />}
        </Indicator>
      </InputWrapper>
      {children && <Text disabled={disabled}>{children}</Text>}
    </Wrapper>
  );
});

Radio.displayName = 'Radio';

Radio.Group = Group;
Radio.Tag = RadioTag;
